import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Typography,
  CircularProgress,
  Autocomplete,
  IconButton
} from '@mui/material';
import { Save, Trash, CirclePlus } from 'lucide-react';
import { saveClient, deleteClient } from '../../../apis/Clients';
import { MastersId } from '../../../constants/constant';
import { FilterFieldSx, DropDownSX, CSS_PRIMARY } from '../../../constants/style';
import { fetchGlobalTypesCombo } from '../../../apis/Masters/GlobalType';
import { fetchAreaListCombo } from '../../../apis/Masters/Area';
import { fetchCityListCombo } from '../../../apis/Masters/City';
import { fetchUsersCombo } from '../../../apis/Masters/Users';
import { fetchClientsById } from '../../../apis/Clients';
import { NumericFormat } from 'react-number-format';
import { fetchDcrClientsById, saveDcrClient, deleteDcrClient } from '../../../apis/DCR/Clients';
import { fetchDcrAreas } from '../../../apis/DCR/Area';
import { fetchDcrCityListCombo } from '../../../apis/DCR/City';
import ConfirmDialog from '../common/ConfirmDialog';
import AddArea from '../Masters/Area/AddArea';
import Tooltip from '../../../utils/Tooltip';
import { isDcrScreen, userScreenAccess } from '../../../utils';
import CommonDialog from '../common/CommonDialog';

const emptyPerson = {
  name: '',
  designation: '',
  contactNo: '',
  followUp: false
};

const emptyForm = {
  clientGUID: 0,
  clientCode: '',
  clientName: '',
  address1: '',
  address2: '',
  address3: '',
  address4: '',
  address5: '',
  areaGUID: 0,
  area: '',
  cityGUID: 0,
  city: '',
  EmailId1: '',
  EmailId2: '',
  orgTypeGUID: 0,
  contactPersonName1: '',
  contactPersonName2: '',
  contactPersonName3: '',
  contactPersonName4: '',
  contactPersonName5: '',
  contactPersonName6: '',
  contactPersonDesignationGUID1: 0,
  contactPersonDesignationGUID2: 0,
  contactPersonDesignationGUID3: 0,
  contactPersonDesignationGUID4: 0,
  contactPersonDesignationGUID5: 0,
  contactPersonDesignationGUID6: 0,
  contactPersonDesignation1: '',
  contactPersonDesignation2: '',
  contactPersonDesignation3: '',
  contactPersonDesignation4: '',
  contactPersonDesignation5: '',
  contactPersonDesignation6: '',
  contactNo1: '',
  contactNo2: '',
  contactNo3: '',
  contactNo4: '',
  contactNo5: '',
  contactNo6: '',
  isFollowUpForContactNo1: false,
  isFollowUpForContactNo2: false,
  isFollowUpForContactNo3: false,
  isFollowUpForContactNo4: false,
  isFollowUpForContactNo5: false,
  isFollowUpForContactNo6: false,
  userGUID: 0,
  createdGUID: 1,
  createdDate: '2026-04-17T05:10:29.110Z',
  modifiedGUID: 1,
  modifiedDate: '2026-04-17T05:10:29.110Z',
  isActive: true,
  dailyCallClientGUID: 0,
  dailyCallClientCode: '',
  dailyCallClientName: '',
  dailyCallAreaGUID: 0,
  dailyCallArea: '',
  dailyCallCityGUID: 0,
  dailyCallCity: '',
  visitingTime: ''
};

const validate = (form, isDcrClient) => {
  const errors = {};
  if (!form.userGUID && !isDcrClient) {
    errors.userGUID = 'Incharge is required.';
  }
  if (!form.clientName.trim()) {
    errors.clientName = 'Client name is required.';
  }
  if (!form.areaGUID) {
    errors.areaGUID = 'Area is required.';
  }
  if (!form.orgTypeGUID) {
    errors.orgTypeGUID = 'Org Type is required.';
  }
  return errors;
};

const AddClient = ({
  isDcrClient = false,
  client = null,
  inChargeOptions = [],
  areasOptions = [],
  citiesOptions = [],
  userAccess = null,
  onSuccess,
  onCancel
}) => {
  const isEditMode = Boolean(client);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [orgTypes, setOrgTypes] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [modifiedAreaOptions, setModifiedAreaOptions] = useState([]);
  const [cityMap, setCityMap] = useState({});
  const [localInChargeOptions, setLocalInChargeOptions] = useState(inChargeOptions);
  const [localAreasOptions, setLocalAreasOptions] = useState(areasOptions);
  const [localCitiesOptions, setLocalCitiesOptions] = useState(citiesOptions);
  const [clientDetailsLoading, setClientDetailsLoading] = useState(false);
  const isDeleteAllowd = isEditMode ? userAccess?.hasDeletePermission : false;
  const isReadonly = isEditMode ? !userAccess?.hasEditPermission : !userAccess?.hasAddPermission;
  const [openAreaDialog, setOpenAreaDialog] = useState(false);
  const isDcrArea = isDcrScreen();
  const userAreaAccess = userScreenAccess('/area');

  // ── Confirm-dialog state ────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: null
  });

  const openConfirm = ({ title, content, onConfirm }) =>
    setConfirmDialog({ open: true, title, content, onConfirm });

  const closeConfirm = () =>
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));

  const deleteHandler = () => {
    if (!userAccess?.hasDeletePermission) return;
    openConfirm({
      title: 'Delete Client Record',
      content: `Are you sure you want to delete the Client record for "${
        form.clientName || 'this client'
      }"? This action cannot be undone.`,
      onConfirm: () => {
        closeConfirm();
        handleDelete();
      }
    });
  };

  useEffect(() => {
    const getClientDetails = async () => {
      if (client) {
        const clientGUID = client?.clientGUID || client?.dailyCallClientGUID;
        const clientData = await fetchClientDetails(clientGUID);
        try {
          setForm({
            ...emptyForm,
            clientGUID: clientData?.clientGUID || clientData?.dailyCallClientGUID || 0,
            clientCode: clientData?.clientCode || clientData?.dailyCallClientCode || '',
            clientName: clientData?.clientName || clientData?.dailyCallClientName || '',
            address1: clientData.address1 || '',
            address2: clientData.address2 || '',
            address3: clientData.address3 || '',
            address4: clientData.address4 || '',
            address5: clientData.address5 || '',
            areaGUID: clientData?.areaGUID || clientData?.dailyCallAreaGUID || 0,
            area: clientData?.area || clientData?.dailyCallArea || '',
            cityGUID: clientData?.cityGUID || clientData?.dailyCallCityGUID || 0,
            city: clientData?.city || clientData?.dailyCallCity || '',
            orgTypeGUID: clientData.orgTypeGUID || 0,
            visitingTime: clientData?.visitingTime || '',
            contactPersonName1: clientData.contactPersonName1 || '',
            contactPersonName2: clientData.contactPersonName2 || '',
            contactPersonName3: clientData.contactPersonName3 || '',
            contactPersonName4: clientData.contactPersonName4 || '',
            contactPersonName5: clientData.contactPersonName5 || '',
            contactPersonName6: clientData.contactPersonName6 || '',
            contactPersonDesignationGUID1: clientData.contactPersonDesignationGUID1 || 0,
            contactPersonDesignationGUID2: clientData.contactPersonDesignationGUID2 || 0,
            contactPersonDesignationGUID3: clientData.contactPersonDesignationGUID3 || 0,
            contactPersonDesignationGUID4: clientData.contactPersonDesignationGUID4 || 0,
            contactPersonDesignationGUID5: clientData.contactPersonDesignationGUID5 || 0,
            contactPersonDesignationGUID6: clientData.contactPersonDesignationGUID6 || 0,
            contactPersonDesignation1: clientData.contactPersonDesignation1 || '',
            contactPersonDesignation2: clientData.contactPersonDesignation2 || '',
            contactPersonDesignation3: clientData.contactPersonDesignation3 || '',
            contactPersonDesignation4: clientData.contactPersonDesignation4 || '',
            contactPersonDesignation5: clientData.contactPersonDesignation5 || '',
            contactPersonDesignation6: clientData.contactPersonDesignation6 || '',
            contactNo1: clientData.contactNo1 || '',
            contactNo2: clientData.contactNo2 || '',
            contactNo3: clientData.contactNo3 || '',
            contactNo4: clientData.contactNo4 || '',
            contactNo5: clientData.contactNo5 || '',
            contactNo6: clientData.contactNo6 || '',
            EmailId1: clientData.EmailId1 || clientData.emailID1 || '',
            EmailId2: clientData.EmailId2 || clientData.emailID2 || '',
            isFollowUpForContactNo1:
              clientData.isFollowUpForContactNo1 !== undefined
                ? clientData.isFollowUpForContactNo1
                : false,
            isFollowUpForContactNo2:
              clientData.isFollowUpForContactNo2 !== undefined
                ? clientData.isFollowUpForContactNo2
                : false,
            isFollowUpForContactNo3:
              clientData.isFollowUpForContactNo3 !== undefined
                ? clientData.isFollowUpForContactNo3
                : false,
            isFollowUpForContactNo4:
              clientData.isFollowUpForContactNo4 !== undefined
                ? clientData.isFollowUpForContactNo4
                : false,
            isFollowUpForContactNo5:
              clientData.isFollowUpForContactNo5 !== undefined
                ? clientData.isFollowUpForContactNo5
                : false,
            isFollowUpForContactNo6:
              clientData.isFollowUpForContactNo6 !== undefined
                ? clientData.isFollowUpForContactNo6
                : false,
            userGUID: clientData.userGUID || 0,
            isActive: clientData.isActive,
            createdDate: clientData.createdDate,
            modifiedDate: clientData.modifiedDate,
            createdGUID: clientData.createdGUID,
            modifiedGUID: clientData.userGUID || 0
          });
        } catch (err) {
          console.log('Error setting client details:', err);
        }
      } else {
        setForm(emptyForm);
      }
      setErrors({});
      setTouched({});
    };
    getClientDetails();
  }, [client]);

  const handleAreaCloseDialog = () => {
    setOpenAreaDialog(false);
  };

  const fetchClientDetails = async (clientGUID) => {
    if (!clientGUID) return null;
    setClientDetailsLoading(true);
    try {
      const response = isDcrClient
        ? await fetchDcrClientsById(clientGUID)
        : await fetchClientsById(clientGUID);
      return response;
    } catch (err) {
      console.log('Error fetching client details:', err);
      return null;
    } finally {
      setClientDetailsLoading(false);
    }
  };

  const fetchDependencies = async () => {
    try {
      const [orgTypes, desigination] = await Promise.all([
        fetchGlobalTypesCombo(MastersId.ORGANIZATION_TYPE),
        fetchGlobalTypesCombo(MastersId.DESIGNATION)
      ]);

      const loadedOrgType = orgTypes || [];
      const loadedDesignation = desigination || [];

      setOrgTypes(loadedOrgType);
      setDesignations(loadedDesignation);
    } catch (err) {
      console.log('Failed to fetch dependencies:', err);
    }
  };

  const fetchDependencies2 = async () => {
    if (inChargeOptions.length === 0 && areasOptions.length === 0 && citiesOptions.length === 0) {
      const fetchOptions = async () => {
        try {
          const [users, areas, cities] = await Promise.all([
            fetchUsersCombo(),
            fetchAreaListCombo(),
            fetchCityListCombo()
          ]);

          setLocalInChargeOptions(users || []);
          setLocalAreasOptions(areas || []);
          setLocalCitiesOptions(cities || []);
        } catch (err) {
          console.log('Failed to fetch options:', err);
        }
      };
      fetchOptions();
    } else {
      // If props are provided, update local state
      if (inChargeOptions.length > 0) setLocalInChargeOptions(inChargeOptions);
      if (areasOptions.length > 0) setLocalAreasOptions(areasOptions);
      if (citiesOptions.length > 0) setLocalCitiesOptions(citiesOptions);
    }
  };

  const fetchDcrDependencies = async () => {
    if (areasOptions.length === 0 && citiesOptions.length === 0) {
      const fetchOptions = async () => {
        try {
          const [areas, cities] = await Promise.all([fetchDcrAreas(2), fetchDcrCityListCombo()]);
          setLocalAreasOptions(areas || []);
          setLocalCitiesOptions(cities || []);
        } catch (err) {
          console.log('Failed to fetch options:', err);
        }
      };
      fetchOptions();
    } else {
      // If props are provided, update local state
      if (inChargeOptions.length > 0) setLocalInChargeOptions(inChargeOptions);
      if (areasOptions.length > 0) setLocalAreasOptions(areasOptions);
      if (citiesOptions.length > 0) setLocalCitiesOptions(citiesOptions);
    }
  };

  const fetchAreaListResponse = async () => {
    if (isDcrArea) {
      const dcrResponse = await fetchDcrAreas(2);
      setLocalAreasOptions(dcrResponse || []);
    } else {
      const response = await fetchAreaListCombo();
      setLocalAreasOptions(response || []);
    }
  };

  const fetchCityListResponse = async () => {
    if (isDcrArea) {
      const dcrResponse = await fetchDcrCityListCombo();
      setLocalCitiesOptions(dcrResponse || []);
    } else {
      const response = await fetchCityListCombo();
      setLocalCitiesOptions(response || []);
    }
  };

  const fetchAreaCityDepadancy = () => {
    fetchAreaListResponse();
    fetchCityListResponse();
  };

  const handleAddArea = () => {
    setOpenAreaDialog(true);
  };

  const updateAreaOptions = () => {
    const cityLookup = {};
    localCitiesOptions.forEach((city) => {
      if (city?.cityGUID || city?.dailyCallCityGUID) {
        cityLookup[city.cityGUID || city.dailyCallCityGUID] = city.city || city.dailyCallCity || '';
      }
    });

    const nextAreas = localAreasOptions.map((area) => {
      const cityName =
        cityLookup[area.cityGUID || area.dailyCallCityGUID] ||
        area.city ||
        area.dailyCallCity ||
        '';
      const label =
        area.area || area.dailyCallArea
          ? `${area.area || area.dailyCallArea}${cityName ? ` - ${cityName}` : ''}`
          : cityName;

      return {
        ...area,
        label
      };
    });

    setCityMap(cityLookup);
    setModifiedAreaOptions(nextAreas);
  };

  useEffect(() => {
    fetchDependencies();
    if (isDcrClient) {
      fetchDcrDependencies();
    } else {
      fetchDependencies2();
    }
  }, []);

  useEffect(() => {
    updateAreaOptions();
  }, [localAreasOptions, localCitiesOptions]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const next = { ...form, [field]: value };
    setForm(next);

    if (touched[field]) {
      const errs = validate(next, isDcrClient);
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(form, isDcrClient);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  const handleDeletePerson = (index) => {
    setForm((prev) => ({
      ...prev,
      persons: prev.persons.filter((_, i) => i !== index)
    }));
  };

  const handleAddPerson = () => {
    setForm((prev) => ({
      ...prev,
      persons: [...prev.persons, { ...emptyPerson }]
    }));
  };

  const handleSave = async () => {
    const allTouched = {
      incharge: true,
      name: true
    };
    setTouched(allTouched);

    const errs = validate(form, isDcrClient);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    const payload = {
      ...form,
      clientName: form.clientName.trim().toUpperCase(),
      ...(isDcrClient && {
        dailyCallClientGUID: client?.dailyCallClientGUID || 0,
        dailyCallClientCode: client?.dailyCallClientCode || '',
        dailyCallClientName: form.clientName.trim().toUpperCase() || '',
        dailyCallAreaGUID: form.areaGUID || 0,
        dailyCallArea: form.area || '',
        dailyCallCityGUID: form.cityGUID || 0,
        dailyCallCity: form.city || ''
      })
    };
    const successMsg = isEditMode ? 'Client updated successfully' : 'Client added successfully';
    const response = isDcrClient
      ? await saveDcrClient(payload, successMsg, '')
      : await saveClient(payload, successMsg, '');
    if (response.success) {
      onSuccess?.();
    }
    setSaving(false);
  };

  const hasErrors = Object.keys(validate(form, isDcrClient)).length > 0;

  const handleCityChange = (event, value) => {
    const cityGUID = value?.cityGUID || value?.dailyCallCityGUID || 0;
    const next = { ...form, cityGUID };
    setForm(next);
  };

  const handleAreaChange = (event, value) => {
    const areaGUID = value?.areaGUID || value?.dailyCallAreaGUID || 0;
    const cityGUID = value?.cityGUID || value?.dailyCallCityGUID || 0;
    const next = { ...form, areaGUID, cityGUID };
    setForm(next);
  };

  const handleInChargeChange = (event, value) => {
    const userGUID = value?.userGUID || 0;
    const next = { ...form, userGUID };
    setForm(next);
  };

  const handleOrgTypeChange = (event, value) => {
    const orgTypeGUID = value?.globalTypeID || 0;
    const next = { ...form, orgTypeGUID };
    setForm(next);
  };

  const handleDesignationChange = (field) => (event, value) => {
    const globalTypeID = value?.globalTypeID || 0;
    setForm((prev) => ({ ...prev, [field]: globalTypeID }));
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const successMsg = `Client deleted successfully`;
      const response = isDcrClient
        ? await deleteDcrClient(client?.dailyCallClientGUID, successMsg, '')
        : await deleteClient(client?.clientGUID, successMsg, '');
      if (response && response.success) {
        onSuccess?.();
      }
    } catch (err) {
      console.log('Failed to delete global type:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Box component="form" noValidate autoComplete="off">
        {clientDetailsLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: 1
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            opacity: clientDetailsLoading ? 0.5 : 1,
            pointerEvents: clientDetailsLoading ? 'none' : 'auto'
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: 'calc(33% - 12px)' },
              display: isDcrClient ? 'none' : 'block'
            }}
          >
            <Autocomplete
              size="small"
              fullWidth
              options={localInChargeOptions}
              value={localInChargeOptions.find((u) => u.userGUID === form.userGUID) || null}
              onChange={handleInChargeChange}
              disablePortal={false}
              getOptionLabel={(option) => option?.userName || ''}
              isOptionEqualToValue={(option, value) => option?.userGUID === value?.userGUID}
              readOnly={isReadonly}
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="In Charge *"
                  placeholder="Select In Charge"
                  onBlur={handleBlur('userGUID')}
                  error={touched.userGUID && Boolean(errors.userGUID)}
                  sx={FilterFieldSx}
                />
              )}
            />
          </Box>

          <Box sx={{ width: { xs: '100%', sm: 'calc(33% - 12px)' } }}>
            <TextField
              fullWidth
              label="Client Name *"
              placeholder="Enter client name"
              value={form.clientName}
              onChange={handleChange('clientName')}
              onBlur={handleBlur('clientName')}
              error={touched.clientName && Boolean(errors.clientName)}
              helperText={touched.clientName && errors.clientName}
              size="small"
              sx={{
                ...FilterFieldSx,
                '& input': {
                  textTransform: 'uppercase'
                }
              }}
              slotProps={{
                input: {
                  readOnly: isReadonly
                }
              }}
            />
          </Box>

          <Box sx={{ width: { xs: '100%', sm: 'calc(33% - 12px)' } }}>
            <TextField
              fullWidth
              label="Address 1"
              placeholder="Enter address line 1"
              value={form.address1}
              onChange={handleChange('address1')}
              size="small"
              sx={FilterFieldSx}
              slotProps={{
                input: {
                  readOnly: isReadonly
                }
              }}
            />
          </Box>

          <Box sx={{ width: { xs: '100%', sm: 'calc(33% - 12px)' } }}>
            <TextField
              fullWidth
              label="Address 2"
              placeholder="Enter address line 2"
              value={form.address2}
              onChange={handleChange('address2')}
              size="small"
              sx={FilterFieldSx}
              slotProps={{
                input: {
                  readOnly: isReadonly
                }
              }}
            />
          </Box>

          {!isDcrClient && (
            <>
              <Box sx={{ width: { xs: '100%', sm: 'calc(33% - 12px)' } }}>
                <TextField
                  fullWidth
                  label="Email ID 1"
                  placeholder="Enter email ID 1"
                  value={form.EmailId1}
                  onChange={handleChange('EmailId1')}
                  size="small"
                  sx={FilterFieldSx}
                  slotProps={{
                    input: {
                      readOnly: isReadonly
                    }
                  }}
                />
              </Box>

              <Box sx={{ width: { xs: '100%', sm: 'calc(33% - 12px)' } }}>
                <TextField
                  fullWidth
                  label="Email ID 2"
                  placeholder="Enter email ID 2"
                  value={form.EmailId2}
                  onChange={handleChange('EmailId2')}
                  size="small"
                  sx={FilterFieldSx}
                  slotProps={{
                    input: {
                      readOnly: isReadonly
                    }
                  }}
                />
              </Box>
            </>
          )}

          <Box
            sx={{
              width: { xs: '100%', sm: 'calc(33.333% - 14px)' },
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}
          >
            <Autocomplete
              size="small"
              fullWidth
              options={modifiedAreaOptions}
              value={
                modifiedAreaOptions.find(
                  (a) => a?.areaGUID === form.areaGUID || a?.dailyCallAreaGUID === form.areaGUID
                ) || null
              }
              onChange={handleAreaChange}
              disablePortal={false}
              readOnly={isReadonly}
              getOptionLabel={(option) =>
                option?.label || option?.area || option?.dailyCallArea || ''
              }
              isOptionEqualToValue={(option, value) =>
                !isDcrClient
                  ? option?.areaGUID === value?.areaGUID
                  : option?.dailyCallAreaGUID === value?.dailyCallAreaGUID
              }
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
              sx={{ flex: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Area *"
                  placeholder="Select Area"
                  onBlur={handleBlur('areaGUID')}
                  error={touched.areaGUID && Boolean(errors.areaGUID)}
                  sx={FilterFieldSx}
                />
              )}
            />

            {userAreaAccess?.hasAddPermission && (
              <Tooltip title="Add Area">
                <IconButton
                  size="small"
                  onClick={() => handleAddArea()}
                  sx={{
                    p: 0.5,
                    color: 'text.secondary',
                    mt: '4px'
                  }}
                >
                  <CirclePlus size={18} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ width: { xs: '100%', sm: 'calc(33.333% - 14px)' } }}>
            <Autocomplete
              size="small"
              readOnly={true}
              fullWidth
              options={localCitiesOptions}
              value={
                localCitiesOptions.find(
                  (c) => c?.cityGUID === form.cityGUID || c?.dailyCallCityGUID === form.cityGUID
                ) || null
              }
              onChange={handleCityChange}
              disablePortal={false}
              getOptionLabel={(option) => option?.city || option?.dailyCallCity || ''}
              isOptionEqualToValue={(option, value) =>
                option?.cityGUID === value?.cityGUID ||
                option?.dailyCallCityGUID === value?.cityGUID
              }
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City *"
                  placeholder="City is read only"
                  sx={FilterFieldSx}
                  onBlur={handleBlur('cityGUID')}
                  error={touched.cityGUID && Boolean(errors.cityGUID)}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: isReadonly
                  }}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              width: { xs: '100%', sm: 'calc(33% - 12px)' },
              display: isDcrClient ? 'block' : 'none'
            }}
          >
            <TextField
              fullWidth
              label="Visiting Time"
              placeholder="Enter visiting time"
              value={form.visitingTime}
              onChange={handleChange('visitingTime')}
              size="small"
              sx={FilterFieldSx}
              slotProps={{
                input: {
                  readOnly: isReadonly
                }
              }}
            />
          </Box>

          <Box sx={{ width: { xs: '100%', sm: 'calc(33.333% - 14px)' } }}>
            <Autocomplete
              size="small"
              fullWidth
              options={orgTypes}
              value={orgTypes.find((o) => o.globalTypeID === form.orgTypeGUID) || null}
              onChange={handleOrgTypeChange}
              disablePortal={false}
              getOptionLabel={(option) => option?.globalTypeName || ''}
              isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
              readOnly={isReadonly}
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organization Type *"
                  placeholder="Select Organization Type"
                  onBlur={handleBlur('orgTypeGUID')}
                  error={touched.orgTypeGUID && Boolean(errors.orgTypeGUID)}
                  sx={FilterFieldSx}
                />
              )}
            />
          </Box>

          <Box sx={{ width: { xs: '100%', sm: 'calc(25% - 14px)' } }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isActive}
                  onChange={handleChange('isActive')}
                  size="small"
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': { color: CSS_PRIMARY }
                  }}
                  readOnly={isReadonly}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Active
                </Typography>
              }
            />
          </Box>
        </Box>

        <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2, fontWeight: 600 }}>
          Contact Persons
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                alignItems: 'center',
                mb: 1
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <TextField
                    fullWidth
                    label={`Person 1 Name`}
                    placeholder="Enter name"
                    value={form.contactPersonName1}
                    onChange={handleChange('contactPersonName1')}
                    size="small"
                    sx={FilterFieldSx}
                    slotProps={{
                      input: {
                        readOnly: isReadonly
                      }
                    }}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={designations}
                    value={
                      designations.find(
                        (o) => o.globalTypeID === form.contactPersonDesignationGUID1
                      ) || null
                    }
                    onChange={handleDesignationChange('contactPersonDesignationGUID1')}
                    readOnly={isReadonly}
                    disablePortal={false}
                    getOptionLabel={(option) => option?.globalTypeName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.globalTypeID === value?.globalTypeID
                    }
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Designation 1"
                        placeholder="Select Designation 1"
                        sx={FilterFieldSx}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <NumericFormat
                    value={form.contactNo1}
                    onChange={handleChange('contactNo1')}
                    customInput={TextField}
                    valueIsNumericString
                    variant="outlined"
                    label="Contact No 1*"
                    fullWidth
                    size="small"
                    sx={FilterFieldSx}
                    isAllowed={(values) => values.value.length <= 10}
                    readOnly={isReadonly}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(16% - 14px)' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isFollowUpForContactNo1}
                        onChange={handleChange('isFollowUpForContactNo1')}
                        size="small"
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': { color: CSS_PRIMARY }
                        }}
                        readOnly={isReadonly}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Follow Up
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: 'background.paper',
                alignItems: 'center',
                mb: 1
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <TextField
                    fullWidth
                    label={`Person 2 Name`}
                    placeholder="Enter name"
                    value={form.contactPersonName2}
                    onChange={handleChange('contactPersonName2')}
                    size="small"
                    sx={FilterFieldSx}
                    slotProps={{
                      input: {
                        readOnly: isReadonly
                      }
                    }}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={designations}
                    value={
                      designations.find(
                        (o) => o.globalTypeID === form.contactPersonDesignationGUID2
                      ) || null
                    }
                    onChange={handleDesignationChange('contactPersonDesignationGUID2')}
                    disablePortal={false}
                    getOptionLabel={(option) => option?.globalTypeName || ''}
                    readOnly={isReadonly}
                    isOptionEqualToValue={(option, value) =>
                      option?.globalTypeID === value?.globalTypeID
                    }
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Designation 2"
                        placeholder="Select Designation 2"
                        sx={FilterFieldSx}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <NumericFormat
                    value={form.contactNo2}
                    onChange={handleChange('contactNo2')}
                    customInput={TextField}
                    valueIsNumericString
                    variant="outlined"
                    label="Contact No 2*"
                    fullWidth
                    size="small"
                    sx={FilterFieldSx}
                    isAllowed={(values) => values.value.length <= 10}
                    readOnly={isReadonly}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(16% - 14px)' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isFollowUpForContactNo2}
                        onChange={handleChange('isFollowUpForContactNo2')}
                        size="small"
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': { color: CSS_PRIMARY }
                        }}
                        readOnly={isReadonly}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Follow Up
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: 'background.paper',
                alignItems: 'center',
                mb: 1
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <TextField
                    fullWidth
                    label={`Person 3 Name`}
                    placeholder="Enter name"
                    value={form.contactPersonName3}
                    onChange={handleChange('contactPersonName3')}
                    size="small"
                    sx={FilterFieldSx}
                    slotProps={{
                      input: {
                        readOnly: isReadonly
                      }
                    }}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={designations}
                    value={
                      designations.find(
                        (o) => o.globalTypeID === form.contactPersonDesignationGUID3
                      ) || null
                    }
                    readOnly={isReadonly}
                    onChange={handleDesignationChange('contactPersonDesignationGUID3')}
                    disablePortal={false}
                    getOptionLabel={(option) => option?.globalTypeName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.globalTypeID === value?.globalTypeID
                    }
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Designation 3"
                        placeholder="Select Designation 3"
                        sx={FilterFieldSx}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <NumericFormat
                    value={form.contactNo3}
                    onChange={handleChange('contactNo3')}
                    customInput={TextField}
                    valueIsNumericString
                    variant="outlined"
                    label="Contact No 3*"
                    fullWidth
                    size="small"
                    sx={FilterFieldSx}
                    isAllowed={(values) => values.value.length <= 10}
                    readOnly={isReadonly}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(16% - 14px)' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isFollowUpForContactNo3}
                        onChange={handleChange('isFollowUpForContactNo3')}
                        size="small"
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': { color: CSS_PRIMARY }
                        }}
                        readOnly={isReadonly}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Follow Up
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: 'background.paper',
                alignItems: 'center',
                mb: 1
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <TextField
                    fullWidth
                    label={`Person 4 Name`}
                    placeholder="Enter name"
                    value={form.contactPersonName4}
                    onChange={handleChange('contactPersonName4')}
                    size="small"
                    sx={FilterFieldSx}
                    slotProps={{
                      input: {
                        readOnly: isReadonly
                      }
                    }}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={designations}
                    value={
                      designations.find(
                        (o) => o.globalTypeID === form.contactPersonDesignationGUID4
                      ) || null
                    }
                    onChange={handleDesignationChange('contactPersonDesignationGUID4')}
                    readOnly={isReadonly}
                    disablePortal={false}
                    getOptionLabel={(option) => option?.globalTypeName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.globalTypeID === value?.globalTypeID
                    }
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Designation 4"
                        placeholder="Select Designation 4"
                        sx={FilterFieldSx}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <NumericFormat
                    value={form.contactNo4}
                    onChange={handleChange('contactNo4')}
                    customInput={TextField}
                    valueIsNumericString
                    variant="outlined"
                    label="Contact No 4*"
                    fullWidth
                    size="small"
                    sx={FilterFieldSx}
                    isAllowed={(values) => values.value.length <= 10}
                    readOnly={isReadonly}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(16% - 14px)' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isFollowUpForContactNo4}
                        onChange={handleChange('isFollowUpForContactNo4')}
                        size="small"
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': { color: CSS_PRIMARY }
                        }}
                        readOnly={isReadonly}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Follow Up
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {!isDcrClient && (
          <>
            <Box
              sx={{
                bgcolor: 'background.paper',
                alignItems: 'center',
                mb: 1
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <TextField
                    fullWidth
                    label={`Person 5 Name`}
                    placeholder="Enter name"
                    value={form.contactPersonName5}
                    onChange={handleChange('contactPersonName5')}
                    size="small"
                    sx={FilterFieldSx}
                    slotProps={{
                      input: {
                        readOnly: isReadonly
                      }
                    }}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={designations}
                    value={
                      designations.find(
                        (o) => o.globalTypeID === form.contactPersonDesignationGUID5
                      ) || null
                    }
                    readOnly={isReadonly}
                    onChange={handleDesignationChange('contactPersonDesignationGUID5')}
                    disablePortal={false}
                    getOptionLabel={(option) => option?.globalTypeName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.globalTypeID === value?.globalTypeID
                    }
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Designation 5"
                        placeholder="Select Designation 5"
                        sx={FilterFieldSx}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <NumericFormat
                    value={form.contactNo5}
                    onChange={handleChange('contactNo5')}
                    customInput={TextField}
                    valueIsNumericString
                    variant="outlined"
                    label="Contact No 5*"
                    fullWidth
                    size="small"
                    sx={FilterFieldSx}
                    isAllowed={(values) => values.value.length <= 10}
                    readOnly={isReadonly}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(16% - 14px)' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isFollowUpForContactNo5}
                        onChange={handleChange('isFollowUpForContactNo5')}
                        size="small"
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': { color: CSS_PRIMARY }
                        }}
                        readOnly={isReadonly}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Follow Up
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: 'background.paper',
                alignItems: 'center',
                mb: 1
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <TextField
                    fullWidth
                    label={`Person 6 Name`}
                    placeholder="Enter name"
                    value={form.contactPersonName6}
                    onChange={handleChange('contactPersonName6')}
                    size="small"
                    sx={FilterFieldSx}
                    slotProps={{
                      input: {
                        readOnly: isReadonly
                      }
                    }}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={designations}
                    value={
                      designations.find(
                        (o) => o.globalTypeID === form.contactPersonDesignationGUID6
                      ) || null
                    }
                    readOnly={isReadonly}
                    onChange={handleDesignationChange('contactPersonDesignationGUID6')}
                    disablePortal={false}
                    getOptionLabel={(option) => option?.globalTypeName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.globalTypeID === value?.globalTypeID
                    }
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Designation 6"
                        placeholder="Select Designation 6"
                        sx={FilterFieldSx}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(28% - 14px)' } }}>
                  <NumericFormat
                    value={form.contactNo6}
                    onChange={handleChange('contactNo6')}
                    customInput={TextField}
                    valueIsNumericString
                    variant="outlined"
                    label="Contact No 6*"
                    fullWidth
                    size="small"
                    sx={FilterFieldSx}
                    isAllowed={(values) => values.value.length <= 10}
                    readOnly={isReadonly}
                  />
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'calc(16% - 14px)' } }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.isFollowUpForContactNo6}
                        onChange={handleChange('isFollowUpForContactNo6')}
                        size="small"
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': { color: CSS_PRIMARY }
                        }}
                        readOnly={isReadonly}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Follow Up
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={
              deleting ? <CircularProgress size={14} color="inherit" /> : <Trash size={15} />
            }
            disabled={deleting}
            onClick={deleteHandler}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: '#e05757',
              '&:hover': { background: '#6d6464' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
              display: !isEditMode || !isDeleteAllowd ? 'none' : 'inline-flex'
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />}
            disabled={saving || (hasErrors && Object.keys(touched).length > 0)}
            onClick={handleSave}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
              display: isReadonly ? 'none' : 'inline-flex'
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>

      {/* ── Add / Edit Dialog ─────────────────────────────────────────── */}
      <CommonDialog
        open={openAreaDialog}
        onClose={handleAreaCloseDialog}
        title="Add Area"
        maxWidth="sm"
        persistent={true}
        fullScreen={false}
      >
        <AddArea
          isDcrArea={isDcrArea}
          areaData={null}
          userAccess={userAreaAccess}
          onRefresh={fetchAreaCityDepadancy}
          onClose={handleAreaCloseDialog}
        />
      </CommonDialog>

      {/* ── Confirm Dialog ─────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        content={confirmDialog.content}
        titleIcon={<Trash size={18} />}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </>
  );
};

export default AddClient;
